import { useMemo, useState } from "react";
import { Check, ChevronDown, Search } from "lucide-react";
import { Button } from "@nosible/shared/components/ui/button";
import { Input } from "@nosible/shared/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@nosible/shared/components/ui/popover";

type EntityPickerOption = {
  value: string;
  label: string;
  searchText?: string;
};

type EntityPickerProps = {
  id: string;
  label: string;
  value: string;
  options: EntityPickerOption[];
  placeholder: string;
  onValueChange: (value: string) => void;
  required?: boolean;
  disabled?: boolean;
};

export function EntityPicker({
  id,
  label,
  value,
  options,
  placeholder,
  onValueChange,
  required = false,
  disabled = false,
}: EntityPickerProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const selected = options.find((option) => option.value === value);
  const filteredOptions = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase();
    if (!normalizedQuery) return options;

    return options.filter((option) =>
      `${option.label} ${option.searchText ?? ""}`.toLocaleLowerCase().includes(normalizedQuery),
    );
  }, [options, query]);

  const select = (nextValue: string) => {
    onValueChange(nextValue);
    setQuery("");
    setOpen(false);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) setQuery("");
      }}
    >
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-label={label}
          aria-required={required}
          aria-expanded={open}
          aria-controls={`${id}-listbox`}
          disabled={disabled}
          className="admin-entity-picker__trigger"
        >
          <span className="admin-entity-picker__value">{selected?.label ?? placeholder}</span>
          <ChevronDown aria-hidden="true" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="admin-entity-picker__content">
        <div className="admin-entity-picker__search">
          <Search aria-hidden="true" />
          <Input
            aria-label={`Search ${label.toLocaleLowerCase()}`}
            autoFocus
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={`Search ${label.toLocaleLowerCase()}…`}
          />
        </div>
        <div id={`${id}-listbox`} role="listbox" aria-label={label} className="admin-entity-picker__options">
          {filteredOptions.length === 0 ? (
            <p className="admin-entity-picker__empty">No matching records.</p>
          ) : filteredOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              role="option"
              aria-selected={option.value === value}
              className="admin-entity-picker__option"
              onClick={() => select(option.value)}
            >
              <span>{option.label}</span>
              {option.value === value && <Check aria-label="Selected" />}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
